import { bool, lazy, number, object, string, array } from "yup";

export let schema = (rate, onsiterate, t, mode) =>
  object({
    firstName: string().required(t("validation:required-field")).trim(),
    lastName: string().required(t("validation:required-field")).trim(),
    userName: (() => {
      let baseSchema = string()
        .trim()
        .required(t("validation:required-field"));

      return baseSchema.max(30, t("validation:username-max-length"));
    })(),
    email: string()
      .required(t("validation:required-field"))
      .matches(
        /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,4}$/,
        t("validation:invalid-phone-number")
      ),
    phoneNumber: string()
      .required(t("validation:required-field"))
      .transform((value) => value.replace(/[-\s]/g, ""))
      .test("hasExactDigits", t("validation:invalid-phone-number"), (value) => {
        if (!value) { return false; }
        const digitCount = value.length;

          // Ensures that exactly 10 digits are present and only numbers are entered.
        return digitCount === 10 && /^\d{10}$/.test(value);
      })
      .trim(),

    verifyDoctor: bool(),
    hourlyRate: lazy((value) =>
      value === ""
        ? string()
        : number()
            .optional()
            .typeError(t("validation:invalid-amount"))
            .max(rate, t("validation:maxium-hourley-exceed"))
            .positive("")
    ),
    onsiteHourlyRate: lazy((value) =>
      value === ""
        ? string()
        : number()
            .optional()
            .typeError(t("validation:invalid-amount"))
            .max(onsiterate, t("validation:maxium-hourley-exceed"))
            .positive("")
    ),

    speciality: array()
      .min(1, t("validation:required-field"))
      .required(t("validation:required-field")),

    specialityRates: object().test(
      "validate-specialty-rates",
      function (value) {
        const { path, createError, parent } = this;
        
        // Get selected specialties
        const selectedSpecialities = parent.speciality;

        if (!Array.isArray(selectedSpecialities) || selectedSpecialities.length === 0) {
          return true;
        }

        for (const spec of selectedSpecialities) {
          const specId = spec.id || spec.typeId; 
          
          const rates = value?.[specId] || {};
          const { onlinePrice, physicalPrice, patientOnlinePrice, patientPhysicalPrice } = rates;

          //Doctor App Online Rate
          const hasOnline = onlinePrice !== "" && onlinePrice !== null && onlinePrice !== undefined;
          
          if (!hasOnline) {
            return createError({
              path: `${path}.${specId}.onlinePrice`, 
              message: t("validation:required-field") 
            });
          }

          if (hasOnline) {
            const onlinePriceNum = parseFloat(onlinePrice);
            if (isNaN(onlinePriceNum) || onlinePriceNum < 0) {
              return createError({
                path: `${path}.${specId}.onlinePrice`,
                message: t("validation:invalid-amount"),
              });
            }
            if (rate && onlinePriceNum > rate) {
              return createError({
                path: `${path}.${specId}.onlinePrice`,
                message: t("validation:maxium-hourley-exceed"),
              });
            }
          }

          // Doctor App Physical Rate
          const hasPhysical = physicalPrice !== "" && physicalPrice !== null && physicalPrice !== undefined;

          if (!hasPhysical) {
            return createError({
              path: `${path}.${specId}.physicalPrice`, 
              message: t("validation:required-field") 
            });
          }

          if (hasPhysical) {
            const physicalPriceNum = parseFloat(physicalPrice);
            if (isNaN(physicalPriceNum) || physicalPriceNum < 0) {
              return createError({
                path: `${path}.${specId}.physicalPrice`,
                message: t("validation:invalid-amount"),
              });
            }
            if (onsiterate && physicalPriceNum > onsiterate) {
              return createError({
                path: `${path}.${specId}.physicalPrice`,
                message: t("validation:maxium-hourley-exceed"),
              });
            }
          }

          // Patient App Online Rate 
          const hasPatientOnline = patientOnlinePrice !== "" && patientOnlinePrice !== null && patientOnlinePrice !== undefined;

          if (!hasPatientOnline) {
            return createError({
              path: `${path}.${specId}.patientOnlinePrice`,
              message: t("validation:required-field")
            });
          }

          if (hasPatientOnline) {
            const patientOnlineNum = parseFloat(patientOnlinePrice);
            if (isNaN(patientOnlineNum) || patientOnlineNum < 0) {
              return createError({
                path: `${path}.${specId}.patientOnlinePrice`,
                message: t("validation:invalid-amount"),
              });
            }
            if (rate && patientOnlineNum > rate) {
              return createError({
                path: `${path}.${specId}.patientOnlinePrice`,
                message: t("validation:maxium-hourley-exceed"),
              });
            }
          }

          // Patient App Physical Rate 
          const hasPatientPhysical = patientPhysicalPrice !== "" && patientPhysicalPrice !== null && patientPhysicalPrice !== undefined;

          if (!hasPatientPhysical) {
            return createError({
              path: `${path}.${specId}.patientPhysicalPrice`,
              message: t("validation:required-field")
            });
          }

          if (hasPatientPhysical) {
            const patientPhysicalNum = parseFloat(patientPhysicalPrice);
            if (isNaN(patientPhysicalNum) || patientPhysicalNum < 0) {
              return createError({
                path: `${path}.${specId}.patientPhysicalPrice`,
                message: t("validation:invalid-amount"),
              });
            }
            if (onsiterate && patientPhysicalNum > onsiterate) {
              return createError({
                path: `${path}.${specId}.patientPhysicalPrice`,
                message: t("validation:maxium-hourley-exceed"),
              });
            }
          }
        }

        return true;
      }
    ),

    street01: lazy((value) => (value != null ? string().trim() : string())),
    street02: lazy((value) => (value != null ? string().trim() : string())),
    city: lazy((value) => (value != null ? string().trim() : string())),
    region: lazy((value) => (value != null ? string().trim() : string())),
    state: lazy((value) => (value != null ? string().trim() : string())),
    postCode: lazy((value) => (value != null ? string().trim() : string())),
    registrationNo: lazy((value) =>
      value != null ? string().trim() : string()
    ),
    ProfessionalLiceneseNumber: lazy((value) =>
      value != null ? string().trim() : string()
    ),
  });
